/**
 * Pictogrammes des onze catégories — EF-IHM-004, DESIGN.md §3.
 *
 * Le **pictogramme** identifie le type de POI ; la **couleur** identifie sa
 * famille de besoin. La couleur vient de `currentColor`, héritée de la ligne :
 * c'est ce qui permet à un même tracé de servir en clair et en sombre.
 *
 * Un POI épinglé est entouré d'un anneau ajouté **par l'interface**, jamais
 * dans le tracé, et conserve la couleur de sa famille.
 */

import type { JSX } from 'preact';
import { categoryLabel, type CategoryKey } from '../core/categories';

/** Grille commune : dessin dans 2–22, trait de 2, extrémités arrondies. */
const COMMON = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': '2',
  'stroke-linecap': 'round' as const,
  'stroke-linejoin': 'round' as const,
};

const PATHS: Readonly<Record<CategoryKey, JSX.Element>> = {
  water: <path d="M12 3c0 0 6.5 7.5 6.5 11.5a6.5 6.5 0 0 1-13 0C5.5 10.5 12 3 12 3Z" />,
  bakery: (
    <>
      <path d="M5.2 18.8c-1.7-1.7-.6-4.8 2.1-7.5l4-4c2.7-2.7 5.8-3.8 7.5-2.1s.6 4.8-2.1 7.5l-4 4c-2.7 2.7-5.8 3.8-7.5 2.1Z" />
      <path d="M8.4 13.2 10.6 15.4M11.2 10.4 13.4 12.6M14 7.6 16.2 9.8" />
    </>
  ),
  convenience: (
    <>
      <path d="M8.5 9V7.5a3.5 3.5 0 0 1 7 0V9" />
      <path d="M3.5 9h17l-1.7 9.4a2 2 0 0 1-2 1.6H7.2a2 2 0 0 1-2-1.6Z" />
    </>
  ),
  food: (
    <>
      <path d="M6.5 3v4.5a2.5 2.5 0 0 0 5 0V3M9 3v4.5M9 10v11" />
      <path d="M15.8 12C15.8 7 17 4 18.5 3v9Zm2.7 0v9" />
    </>
  ),
  vending: (
    <>
      <rect x="2.5" y="4" width="19" height="16" rx="1.8" />
      <path d="M14 4v16" />
      <path d="M5.5 8h5M5.5 12h5M5.5 16h5" />
      <path d="M17 7.5h2" />
      <rect x="16" y="14.5" width="4" height="3" />
    </>
  ),
  fuel: (
    <>
      <path d="M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M2.5 21h13M6.5 6.5h5v3.5h-5Z" />
      <path d="M14 10h3a2 2 0 0 1 2 2v4.6a1.7 1.7 0 0 0 3.4 0V9l-2.6-2.6" />
    </>
  ),
  hotel: <path d="M3 20V6M3 11h13a5 5 0 0 1 5 5v4M3 16h18M6.5 8.5h3.5" />,
  guesthouse: (
    <>
      <path d="M2.5 11.5 12 4.5l9.5 7" />
      <path d="M5.5 10V20h13V10M10 20v-5.5h4V20" />
      <path d="M16.5 7.2V3.5h2.6v5.6" />
    </>
  ),
  campsite: (
    <>
      <path d="M12 4 3.5 20h17Z" />
      <path d="M12 9.5 9 20M12 9.5 15 20M3.5 20 1.8 16.5M20.5 20l1.7-3.5" />
    </>
  ),
  shelter: (
    <>
      <path d="M2 11 12 4.5 22 11" />
      <path d="M5.5 11.5V20M18.5 11.5V20M3 20h18" />
    </>
  ),
  checkpoint: (
    <>
      <rect x="6" y="3.5" width="12" height="7" rx="1.8" />
      <path d="M6 7h12M12 10.5V21M8.5 21h7" />
    </>
  ),
};

export interface CategoryIconProps {
  readonly category: CategoryKey;
  readonly size?: number;
  /** Ajoute l'anneau d'épinglage autour du pictogramme. */
  readonly pinned?: boolean;
  /** Rend l'icône décorative lorsque le nom accessible est porté ailleurs. */
  readonly decorative?: boolean;
}

/**
 * Chaque icône possède un nom accessible donnant le type et, le cas échéant,
 * l'état « épinglé ».
 */
export function CategoryIcon(props: CategoryIconProps): JSX.Element {
  const size = props.size ?? 28;
  const label = props.pinned === true
    ? `${categoryLabel(props.category)}, épinglé`
    : categoryLabel(props.category);

  return (
    <svg
      {...COMMON}
      width={size}
      height={size}
      class={props.pinned === true ? 'icon icon--pinned' : 'icon'}
      role={props.decorative === true ? 'presentation' : 'img'}
      aria-label={props.decorative === true ? undefined : label}
      aria-hidden={props.decorative === true ? 'true' : undefined}
    >
      {PATHS[props.category]}
    </svg>
  );
}

/** Anneau d'épinglage, dessiné autour de l'icône par l'interface. */
export function PinRing(props: { readonly children: JSX.Element }): JSX.Element {
  return <span class="pin-ring">{props.children}</span>;
}

export type GlyphName =
  | 'depart'
  | 'pause'
  | 'sommeil'
  | 'arrivee'
  | 'zone'
  | 'navigation'
  | 'retour'
  | 'fermer'
  | 'verrou'
  | 'actions'
  | 'deplier'
  | 'importer'
  | 'exporter'
  | 'ajouter'
  | 'filtres'
  | 'course'
  | 'calculer'
  | 'carte'
  | 'suivant'
  | 'loupe'
  | 'verrou-ouvert'
  | 'position-ok'
  | 'position-ko';

const GLYPHS: Readonly<Record<GlyphName, JSX.Element>> = {
  depart: <path d="M5 12h14M13 6l6 6-6 6" />,
  pause: <path d="M9 5v14M15 5v14" />,
  sommeil: <path d="M20 14.5A8 8 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />,
  arrivee: <path d="M6 3v18M6 4h12l-3 4 3 4H6" />,
  zone: <path d="M12 3v10M12 17v.5M3.5 20.5h17L12 4Z" />,
  navigation: <path d="M3 11l18-7-7 18-2.5-8.5Z" />,
  retour: <path d="M19 12H5M11 6l-6 6 6 6" />,
  fermer: <path d="M6 6l12 12M18 6 6 18" />,
  verrou: <path d="M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5Z" />,
  actions: <path d="M4 7h16M4 12h16M4 17h16" />,
  deplier: <path d="M6 9l6 6 6-6" />,
  importer: (
    <>
      <path d="M12 20V5M6 11l6-6 6 6" />
      <path d="M4 20h16" />
    </>
  ),
  exporter: (
    <>
      <path d="M12 4v15M6 13l6 6 6-6" />
      <path d="M4 20h16" />
    </>
  ),
  ajouter: <path d="M12 5v14M5 12h14" />,
  filtres: <path d="M3 6h18M6 12h12M10 18h4" />,
  course: (
    <>
      <circle cx="6" cy="17.5" r="3.2" />
      <circle cx="18" cy="17.5" r="3.2" />
      <path d="M6 17.5 10 9h4l4 8.5M10 9l-1.5-3H6M14 9h3.5" />
    </>
  ),
  calculer: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  carte: (
    <>
      <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5Z" />
      <path d="M9 4v13M15 6.5v13" />
    </>
  ),
  suivant: <path d="M9 5l7 7-7 7" />,
  loupe: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 21 21" />
    </>
  ),
  'verrou-ouvert': <path d="M7 11V8a5 5 0 0 1 9-3M5 11h14v10H5Z" />,
  'position-ok': <path d="M3 11l18-7-7 18-2.5-8.5Z" />,
  'position-ko': (
    <>
      <path d="M3 11l18-7-7 18-2.5-8.5Z" />
      <path d="M4 4l16 16" />
    </>
  ),
};

export interface GlyphProps {
  readonly name: GlyphName;
  readonly label: string;
  readonly size?: number;
  readonly decorative?: boolean;
}

export function Glyph(props: GlyphProps): JSX.Element {
  const size = props.size ?? 24;
  return (
    <svg
      {...COMMON}
      width={size}
      height={size}
      class="glyph"
      role={props.decorative === true ? 'presentation' : 'img'}
      aria-label={props.decorative === true ? undefined : props.label}
      aria-hidden={props.decorative === true ? 'true' : undefined}
    >
      {GLYPHS[props.name]}
    </svg>
  );
}
